import {
  FunctionComponent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Challenge,
  Environment,
  SteppedUpInterfaceWrapper,
  StepUpIntegrationName,
  useStepUp,
} from '@1debit/step-up';
import { BuildOptions } from '@1debit/step-up/dist/types/state';
import { yupResolver } from '@hookform/resolvers/yup';
import { AnimatePresence } from 'framer-motion';
import getConfig from 'next/config';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { AnyObjectSchema } from 'yup';

import { Button, UnderlinedBoldGhostButton } from 'chime-ui/components/Button';
import Checkbox from 'chime-ui/components/Checkbox/Checkbox';
import { CardBackIcon } from 'chime-ui/components/Icons';
import { useBottomSheet, useErrorDialog } from 'chime-ui/components/Modal';
import InlineErrorMessage from 'chime-ui/components/TextField/InlineErrorMessage';
import TextField from 'chime-ui/components/TextField/TextField';
import {
  SmallTitle,
  SmallTitleSpaced,
  StandardText,
  Text,
} from 'chime-ui/components/Typography';

import Layout, { ContentWrapper, SpacedContent } from 'components/Layout';
import Header from 'components/Layout/Header';
import CardForm from 'components/SubPages/ClaimInfo/CardForm';
import useBlinkCardDialog from 'components/SubPages/ClaimInfo/features/useBlinkCardDialog';
import useClaimInfoBaseForm from 'components/SubPages/ClaimInfo/useClaimInfoBaseForm';

import { useUserContext } from 'hooks/useUserContext';

import { createNudataScript } from './utils/nudata/createNudataScript';
import { schema } from './validation';

type ClaimInfoBaseProps = {
  children: ReactNode;
  hideSaveCardCheckbox?: boolean;
  claimCTACopy: string;
  header?: ReactElement;
  formValidationSchema?: AnyObjectSchema;
};

const ClaimInfoBase: FunctionComponent<ClaimInfoBaseProps> = ({
  children,
  hideSaveCardCheckbox = false,
  claimCTACopy,
  header = <Header showLogin={true} />,
  formValidationSchema = schema,
}) => {
  const [userData] = useUserContext();
  const { publicId } = userData;

  const {
    publicRuntimeConfig: { deployEnvironment },
  } = getConfig();

  const {
    register,
    formState: { errors, isValid, dirtyFields, isSubmitted },
    handleSubmit,
    setFocus,
    setValue,
    reset,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(formValidationSchema),
  });

  const resetFormState = () => {
    reset();
    showStepUpErrorBottomSheet();
  };

  const STEP_UP_ERROR_MESSAGE =
    'Submit the transaction again or try a different debit card.';

  const buildOptions: BuildOptions = {
    integrationName: StepUpIntegrationName.Rfa,
    hideFailureView: false,
    inline: true,
    environment:
      deployEnvironment === 'production'
        ? Environment.Production
        : Environment.NonProduction,
    onSuccess: async (challengeResponseContext: string) => {
      await handleSubmit(
        submitFormHandlerWithChallengeResponseContext(challengeResponseContext)
      )();
    },
    onCancel: () => {
      resetStepUp();
    },
    onFailure: () => {
      resetFormState();
    },
    onError: () => {
      resetFormState();
    },
  };
  const {
    start: startStepUp,
    props: challengeProps,
    reset: resetStepUp,
  } = useStepUp(buildOptions);

  const setChallengeContext = (challengeRequestContext: string) => {
    startStepUp({
      challengeRequestContext,
    });
  };

  const {
    Modal: BlinkCardDialog,
    isShowing: blinkCardDialogIsShowing,
    handleShow: showBlinkCardDialog,
    handleDismiss: dismissBlinkCardDialog,
    errorMessage: blinkCardDialogErrorMessage,
    isOcrAvailable,
  } = useBlinkCardDialog({
    onError: () => {
      showErrorDialog();
    },
  });

  const [isCameraButtonShowing, setCameraButtonShowing] = useState(
    isOcrAvailable
  );

  const handleCameraButtonVisibility = (cardNumber: string) => {
    if (!isOcrAvailable) {
      return;
    }

    // 19 because card length is 16 + the 3 spaces
    // EX: 4444111199993333 transforms to 4444 1111 9999 3333
    if (cardNumber.length < 19) {
      return setCameraButtonShowing(true);
    }

    setCameraButtonShowing(false);
  };

  const {
    submitForm,
    hasError,
    errorMessage,
    errorId,
    isLoading,
    dismissError,
    trackCardNumber,
    trackCVV,
    trackExpDate,
    trackZipCode,
    trackFirstName,
    trackLastName,
    trackEmail,
    trackOnOcrButtonClick,
    trackOnSuccessfulOcrScan,
    trackCardNumberOcr,
  } = useClaimInfoBaseForm(setChallengeContext);

  const submitFormHandlerWithChallengeResponseContext = useCallback(
    (challengeResponseContext?: string) => {
      return async data => {
        const formData = data;

        if (challengeResponseContext) {
          formData.challengeResponseContext = challengeResponseContext;
        }

        await submitForm(formData);
      };
    },
    [submitForm]
  );

  const handleOnOcrClick = () => {
    showBlinkCardDialog();
    trackOnOcrButtonClick();
  };

  const {
    Modal: BottomSheet,
    isShowing: bottomSheetIsShowing,
    handleShow: showBottomSheet,
    handleDismiss: dismissBottomSheet,
  } = useBottomSheet();

  const {
    Modal: StepUpErrorBottomSheet,
    isShowing: stepUpErrorBottomSheetIsShowing,
    handleShow: showStepUpErrorBottomSheet,
  } = useBottomSheet();

  const {
    Modal: ErrorDialog,
    isShowing: errorDialogIsShowing,
    handleShow: showErrorDialog,
  } = useErrorDialog({
    onDismiss: () => {
      dismissError();
    },
  });

  useEffect(() => {
    createNudataScript();
  });

  useEffect(() => {
    if (hasError && errorMessage) showErrorDialog();
  }, [errorMessage, hasError, showErrorDialog]);

  // Removes Card Number and resets form.
  // Stops card validation upon re-entry of card number
  useEffect(() => {
    if (hasError && errorId === 'debit_card_not_eligible') {
      setFocus('cardnumber');
      reset({ cardnumber: '' }, { keepDirty: true, keepErrors: true });
    }
  }, [hasError, errorId, reset, setFocus]);

  useEffect(() => {
    setFocus('firstName');
  }, [setFocus]);

  const handleDebitCardScan = result => {
    dismissBlinkCardDialog();

    if (!result) {
      return;
    }

    const isPartialScan =
      !result?.cardNumber ||
      !result?.cvv ||
      !result?.expiryDate?.originalString;
    trackOnSuccessfulOcrScan(isPartialScan);

    if (result?.cardNumber) {
      trackCardNumberOcr(result?.cardNumber);
    }

    setValue('cardnumber', result?.cardNumber || '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('csc', result?.cvv || '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('expdate', result?.expiryDate?.originalString || '', {
      shouldValidate: true,
      shouldDirty: true,
    });

    setCameraButtonShowing(false);
  };

  const isFormInvalidOrIncomplete = isSubmitted
    ? !isValid
    : !dirtyFields.firstName ||
      !dirtyFields.lastName ||
      !dirtyFields.cardnumber ||
      !dirtyFields.expdate ||
      !dirtyFields.csc ||
      !dirtyFields.postalCode ||
      !dirtyFields.terms;

  const isSubmitButtonDisabled =
    isLoading || isFormInvalidOrIncomplete || !dirtyFields.email;

  return (
    <Layout header={header}>
      <StepUpChallengeWrapper>
        <Challenge {...challengeProps} />
      </StepUpChallengeWrapper>
      <SteppedUpInterfaceWrapper {...challengeProps}>
        <form
          onSubmit={handleSubmit(
            submitFormHandlerWithChallengeResponseContext()
          )}
        >
          <ContentWrapper>
            <SpacedContent>
              {children}

              {/**  PersonalInfo code starts here */}
              <NameRow>
                <TextField
                  {...register('firstName')}
                  id={'firstName'}
                  label={'First name'}
                  hasError={!!errors.firstName && isSubmitted}
                  onBlur={e => trackFirstName(e, publicId)}
                  onAutoFill={e => trackFirstName(e, publicId)}
                />

                <TextField
                  {...register('lastName')}
                  id={'lastName'}
                  label={'Last name'}
                  hasError={!!errors.lastName && isSubmitted}
                  onBlur={e => trackLastName(e, publicId)}
                  onAutoFill={e => trackLastName(e, publicId)}
                />
              </NameRow>

              <AnimatePresence>
                {!!errors.firstName && isSubmitted && (
                  <InlineErrorMessage key={'firstName'}>
                    {errors.firstName?.message}
                  </InlineErrorMessage>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {!!errors.lastName && isSubmitted && (
                  <InlineErrorMessage key={'lastName'}>
                    {errors.lastName?.message}
                  </InlineErrorMessage>
                )}
              </AnimatePresence>

              <EmailRow>
                <TextField
                  {...register('email')}
                  id={'email'}
                  label={'Email address'}
                  autoCapitalize="none"
                  inputMode="email"
                  type="email"
                  hasError={!!errors.email && isSubmitted}
                  onBlur={e => trackEmail(e, publicId)}
                  onAutoFill={e => trackEmail(e, publicId)}
                />
              </EmailRow>

              <AnimatePresence>
                {!!errors.email && isSubmitted && (
                  <InlineErrorMessage key={'email'}>
                    {errors.email?.message}
                  </InlineErrorMessage>
                )}
              </AnimatePresence>
              {/**  PersonalInfo code ends here */}

              <CardForm
                publicId={publicId}
                register={register}
                setValue={setValue}
                errors={errors}
                isSubmitted={isSubmitted}
                trackCVV={trackCVV}
                trackExpDate={trackExpDate}
                trackCardNumber={trackCardNumber}
                trackZipCode={trackZipCode}
                showBottomSheet={showBottomSheet}
                handleOnOcrClick={handleOnOcrClick}
                isCameraButtonShowing={isCameraButtonShowing}
                setCameraButtonShowing={handleCameraButtonVisibility}
              />

              <CheckboxLayout>
                {!hideSaveCardCheckbox && (
                  <Checkbox
                    {...register('cardSave')}
                    id={'cardSave'}
                    defaultChecked={false}
                  >
                    <Text size={'small'}>
                      <Text bold>
                        Use this card for faster cash-outs next time
                      </Text>
                      <br />
                      🔒 Your info is always secure and never shared
                    </Text>
                  </Checkbox>
                )}

                <Checkbox
                  {...register('terms')}
                  id={'terms'}
                  defaultChecked={false}
                >
                  <Text size={'small'}>
                    I have read and agree to the Chime{' '}
                    <UnderlinedBoldGhostButton
                      as={'a'}
                      href="https://www.chime.com/policies/chime/pay-friends-non-chime-member-tc"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms and Conditions
                    </UnderlinedBoldGhostButton>{' '}
                    and{' '}
                    <UnderlinedBoldGhostButton
                      as={'a'}
                      href="https://www.chime.com/policies/chime/privacy-policy/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </UnderlinedBoldGhostButton>
                  </Text>
                </Checkbox>
              </CheckboxLayout>

              <Button
                aria-label={'submit button'}
                type="submit"
                loading={isLoading}
                disabled={isSubmitButtonDisabled}
              >
                {claimCTACopy}
              </Button>
            </SpacedContent>
          </ContentWrapper>
        </form>
      </SteppedUpInterfaceWrapper>
      <AnimatePresence>
        {errorDialogIsShowing && (
          <ErrorDialog>
            {errorMessage || blinkCardDialogErrorMessage}
          </ErrorDialog>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {stepUpErrorBottomSheetIsShowing && (
          <StepUpErrorBottomSheet handleDismiss={resetStepUp}>
            <SmallTitle>Something went wrong</SmallTitle>
            <StandardText>{STEP_UP_ERROR_MESSAGE}</StandardText>
          </StepUpErrorBottomSheet>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {bottomSheetIsShowing && (
          <BottomSheet handleDismiss={dismissBottomSheet}>
            <SmallTitleSpaced>CVV</SmallTitleSpaced>
            <CardBackIcon />
          </BottomSheet>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {blinkCardDialogIsShowing && (
          <BlinkCardDialog onScanSuccess={handleDebitCardScan} />
        )}
      </AnimatePresence>
    </Layout>
  );
};

const NameRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 0.5rem;
  padding-top: 1rem;
`;

const EmailRow = styled.div`
  padding: 0.5rem 0 0 0;
`;

const CheckboxLayout = styled.div`
  padding-top: 1rem;
`;

const StepUpChallengeWrapper = styled.div`
  &:empty {
    display: none;
  }
  padding-top: 30px;
  padding-bottom: 70px;
`;

export default ClaimInfoBase;
